<template>
  <component v-if="template" :is="template" :info />
</template>

<script setup>
import { getGoogleTemplatesInfo } from "@/api/google";
import { isInPWA, isInAndroidApp, isFacebookApp } from "@/env";
import { launchChrome } from "@/utils";

const info = ref(null);
const template = shallowRef(null);
const id = useRoute().query.tid;
const page = useRoute().query.pwaredirect;

const templates = {
  GoogleStore: defineAsyncComponent(() => import("./store.vue")),
  GoogleVideo: defineAsyncComponent(() => import("./video.vue")),
};

onMounted(() => {
  if (page) return

  if (isFacebookApp) {
    launchChrome(location.href)
  }

  if (!isInPWA && !isInAndroidApp && !isFacebookApp) {
    showLoadingToast("loading...");
  }

  if (id && !isInPWA && !isInAndroidApp) {
    getGoogleTemplatesInfo({ id }).then(async ({ code, data }) => {
      closeToast();
      console.log("data", data);
      if (code === 1 && data) {
        info.value = data;
        template.value = templates[data.type] || null;
        // template.value = templates.GoogleVideo;

        if (!template.value) {
          showFailToast("Invalid template");
        }
      } else {
        showFailToast({
          message: 'error',
          duration: 0
        });
      }
    });
  } else {
    // if (!isInPWA && !isInAndroidApp) {
    //   showFailToast("Invalid tid");
    // }
  }
})
</script>

<style lang="scss">
body{
  --van-border-radius-base: 0;
  --van-field-icon-size: 24px;
  --van-field-input-text-color: #ffffff;

  --van-nav-bar-title-text-color: #ffffff;
  --van-nav-bar-icon-color: #ffffff;
  --van-nav-bar-background: #2c3235;
  --van-border-width: 0;
  --van-nav-bar-height: 52px;

  --van-popup-close-icon-margin: 12px;
  --van-popup-background: transparent;
  --van-image-placeholder-background: var(--theme-gray-green20);

  // --focus-bg-color: #2C7952B2;
  --theme-gray-green: #2c7952;
  --theme-gray-green70: #2c7952b2;
  --theme-gray-green40: #2c795266;
  --theme-gray-green20: #2c795233;
  --theme-gray-green15: #2c795226;
  --theme-gray-green10: #2c79521a;

  --theme-red: #ff1241;
  --theme-green: #64f765;
  --theme-green-dark: #151a1f;
  --theme-yellow: #ffdf12;
  --theme-orange: #f8ab3a;
  --theme-gray75: #ffffffbf;
  --theme-gray50: #ffffff80;
  --theme-gray10: #ffffff1a;

  --theme-primary: var(--theme-green);
  --theme-primary-dark: var(--theme-green-dark);
  --theme-primary-hilight: var(--theme-red);
  --theme-primary-error: var(--theme-red);
  --theme-primary-gray: var(--theme-gray75);

  --theme-secondary: var(--theme-green-dark);
  --theme-text-dark: var(--theme-secondary);

  --navbar-bg: var(--theme-gray10);

  // --theme-primary-warn: #FF1241;

  --btn-bg-color: var(--theme-primary);
  --btn-text-color: var(--theme-primary-dark);

  --btn-disabled-bg-color: transparent;
  --btn-disabled-text-color: var(--theme-primary-gray);
  --btn-disabled-border-color: var(--theme-primary-gray);
  
  --input-bg-color: var(--theme-gray-green40);
  --input-focus-bg-color: var(--theme-gray-green70);
  --input-tip-bg-color: var(--theme-gray-green20);

  --title-bg-color: var(--theme-gray-green70);
  --odd-bg-color: var(--theme-gray-green40);
  --even-bg-color: var(--theme-gray-green20);

  --van-cell-background: var(--input-bg-color);

  --dataTable-bg-color-head: var(--theme-gray-green70);
  --dataTable-bg-color-odd: var(--theme-gray-green40);
  --dataTable-bg-color-even: var(--theme-gray-green20);
  --dataTable-line-height: 44px;
  --dataTable-head-height: 44px;
  --dataTable-table-radius: 8px;
  --dataTable-cell-radius: 4px;

  --login-bg: url('@a/assets/images/login.png');
  --reg-bg: url('@a/assets/images/reg.png');

  --receber-btn: url('@a/assets/images/bn_bonus.png');
  --receber-btn-disabled: url('@a/assets/images/bn_bonus2.png');

  --listItem-bg-color-bonus: var(--theme-gray-green40);
  --listItem-bg-color-ggc: var(--theme-gray-green20);
}

.btn {
  width: 100%;
  height: inherit;
  color: var(--btn-text-color);
  background-color: var(--btn-bg-color);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: inherit;
  font-weight: inherit;
  border: none;
}

.btn_transparent,
.btn_disabled,
.btn:disabled {
  @extend .btn;

  background-color: transparent;
  color: currentColor;
  border: 1px solid currentColor;
}

.primaryBtn {
  @extend .btn;

  width: 100%;
  height: 44px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.5px;

  .van-button__text {
    font-weight: 600;
  }
}

.primaryBtn_transparent,
.primaryBtn_disabled,
.primaryBtn:disabled {
  @extend .btn_disabled;

  width: 100%;
  height: 44px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.5px;

  .van-button__text {
    font-weight: 600;
  }
}

.primaryBtn-sm {
  @extend .primaryBtn;

  width: auto;
  height: inherit;
  font-size: inherit;
  font-weight: inherit;
  letter-spacing: 0.3px;

  .van-button__text {
    font-weight: inherit;
  }
}

.primaryBtn_transparent-sm,
.primaryBtn_disabled-sm,
.primaryBtn-sm:disabled {
  @extend .primaryBtn_disabled;

  width: auto;
  height: inherit;
  font-size: inherit;
  font-weight: inherit;
  letter-spacing: 0.3px;

  .van-button__text {
    font-weight: inherit;
  }
}

.primaryBtn-thin {
  @extend .primaryBtn-sm;

  letter-spacing: 0.1px;

  .van-button__text {
    font-weight: inherit;
  }
}

.primaryBtn_transparent-thin,
.primaryBtn_disabled-thin,
.primaryBtn-thin:disabled {
  @extend .primaryBtn_disabled-sm;

  letter-spacing: 0.1px;

  .van-button__text {
    font-weight: inherit;
  }
}

</style>